import { z } from "zod";
import { TaskGroupBy } from "@common/types/task";
import type { ViewOptions } from "@common/types/views/views";
import { ENTITY_LANE_BY } from "./taskBoardLanes";

/** The swimlanes Board offers; null is none. */
export const taskBoardLaneBySchema = z
    .union([z.nativeEnum(TaskGroupBy), z.literal(ENTITY_LANE_BY)])
    .nullable();

/** The board display settings a task view keeps, in either layout. */
export interface TaskViewBoardDisplay {
    /** The swimlanes. */
    groupBy: z.infer<typeof taskBoardLaneBySchema>;
    isEmptyColumnsShown: boolean;
    /** Whether cards show the chat chip (the gear's Comments field). */
    isCommentsShown: boolean;
}

const DEFAULT_DISPLAY: TaskViewBoardDisplay = {
    groupBy: null,
    isEmptyColumnsShown: true,
    isCommentsShown: true,
};

/**
 * The board display a view opens with. A view saved before Board has no
 * swimlanes, and swimlanes this page doesn't offer fall back to none.
 */
export const getTaskViewBoardDisplay = (
    options: ViewOptions | null | undefined,
): TaskViewBoardDisplay => ({
    groupBy: taskBoardLaneBySchema
        .catch(DEFAULT_DISPLAY.groupBy)
        .parse(options?.board?.laneBy ?? DEFAULT_DISPLAY.groupBy),
    isEmptyColumnsShown:
        options?.board?.isEmptyColumnsShown ??
        DEFAULT_DISPLAY.isEmptyColumnsShown,
    isCommentsShown:
        options?.board?.isCommentsShown ?? DEFAULT_DISPLAY.isCommentsShown,
});

/**
 * A view's options with a board display change. Defaults are left out, so
 * returning to them matches a view saved before Board and the view no
 * longer shows unsaved changes; a view without options stays without them.
 */
export const withTaskViewBoardDisplay = ({
    options,
    change,
}: {
    options: ViewOptions | null | undefined;
    change: Partial<TaskViewBoardDisplay>;
}): ViewOptions | null | undefined => {
    const { groupBy, isEmptyColumnsShown, isCommentsShown } = {
        ...getTaskViewBoardDisplay(options),
        ...change,
    };
    const { board: _board, ...otherOptions } = options ?? {};
    const board = {
        ...(groupBy !== DEFAULT_DISPLAY.groupBy && { laneBy: groupBy }),
        ...(isEmptyColumnsShown !== DEFAULT_DISPLAY.isEmptyColumnsShown && {
            isEmptyColumnsShown,
        }),
        ...(isCommentsShown !== DEFAULT_DISPLAY.isCommentsShown && {
            isCommentsShown,
        }),
    };
    const hasBoard = Object.keys(board).length > 0;
    if (!options && !hasBoard) {
        return options;
    }
    return { ...otherOptions, ...(hasBoard && { board }) };
};
